<template>
  <div :class="$style.page">
    <div :class="$globalStyle.headerContainer">
      <HeaderActionButton
        text="Back"
        :iconLink="leftArrowIcon"
        :clickFunction="goBack"
        backBg="#000000"
        frontBg="#676767"
        textColor="#FFFFFF"
        :button-width="'130px'"
      />
      <div :class="$globalStyle.header">
        <div :class="$globalStyle.headerLeft">
          <img :src="saboteurLogo" alt="icon" :class="$globalStyle.headerLogo" />
          <div :class="$globalStyle.headerTitle">{{ $t("saboteur") }} - {{ roleLabel }}</div>
        </div>
        <div :class="$globalStyle.headerRight">
          <HeaderLanguageSelector />
        </div>
      </div>
    </div>
    <div :class="$style.bodyContainer">
      <!-- List view: Share game = Personal game share | Group game share (centered) -->
      <template v-if="!groupId">
        <div :class="$style.shareGameWrap">
          <h1 :class="$style.pageTitle">Share game</h1>
          <div :class="$style.shareGameChoiceRow">
            <button
              :class="[$style.shareGameChoiceCard, mainTab === 'sharedWithMe' && $style.shareGameChoiceCardActive]"
              @click="mainTab = 'sharedWithMe'; loadSharedWithMe()"
            >
              <span :class="$style.shareGameChoiceLabel">Personal game share</span>
              <span :class="$style.shareGameChoiceDesc">Games shared with you by email</span>
            </button>
            <span :class="$style.shareGameDivider">|</span>
            <button
              :class="[$style.shareGameChoiceCard, mainTab === 'groups' && $style.shareGameChoiceCardActive]"
              @click="mainTab = 'groups'"
            >
              <span :class="$style.shareGameChoiceLabel">Group game share</span>
              <span :class="$style.shareGameChoiceDesc">Create or join groups to share games</span>
            </button>
          </div>

          <!-- Personal game share content: Game shared to me | Share game other collaborators -->
          <div v-show="mainTab === 'sharedWithMe'" :class="$style.tabPanel">
            <div :class="$style.personalSubChoiceRow">
              <button
                :class="[$style.personalSubChoiceCard, personalSubTab === 'sharedToMe' && $style.personalSubChoiceCardActive]"
                @click="personalSubTab = 'sharedToMe'"
              >
                Game shared to me
              </button>
              <button
                :class="[$style.personalSubChoiceCard, personalSubTab === 'shareToOthers' && $style.personalSubChoiceCardActive]"
                @click="personalSubTab = 'shareToOthers'; loadMyGamesForShare()"
              >
                Share game other {{ collaboratorLabelPlural }}
              </button>
            </div>

            <!-- Game shared to me: list -->
            <div v-show="personalSubTab === 'sharedToMe'" :class="$style.panelCard">
              <div v-if="sharedWithMeLoading" :class="$style.loading">Loading...</div>
              <div v-else-if="sharedWithMeError" :class="$style.errorText">{{ sharedWithMeError }}</div>
              <div v-else-if="sharedWithMeSessions.length" :class="$style.sessionList">
                <div
                  v-for="s in sharedWithMeSessions"
                  :key="s.gameId"
                  :class="$style.sessionRow"
                  @click="viewSession(s)"
                >
                  <div :class="$style.sessionInfo">
                    <span :class="$style.sessionGameId">{{ (s.title && s.title.trim()) ? s.title.trim() : "Untitled" }}</span>
                    <span :class="$style.sessionMeta">{{ s.hostUsername }}</span>
                    <span v-if="s.sharedByUsername" :class="$style.sessionMeta"> · shared by {{ s.sharedByUsername }}</span>
                  </div>
                  <button :class="$style.viewBtn" @click.stop="viewSession(s)">View</button>
                </div>
              </div>
              <p v-else :class="$style.noGames">No games shared with you yet.</p>
            </div>

            <!-- Share game other collaborators: email + game pick -->
            <div v-show="personalSubTab === 'shareToOthers'" :class="$style.panelCard">
              <label :class="$style.personalShareLabel">Recipient email</label>
              <input
                v-model="personalShareEmail"
                type="email"
                placeholder="colleague@example.com"
                :class="$style.personalShareInput"
              />
              <div v-if="myGamesForShare.length" :class="[$style.gamePickList, $style.shareModalGamePickList]">
                <div
                  v-for="g in myGamesForShare"
                  :key="g.gameId"
                  :class="[$style.gamePickItem, personalShareGameId === g.gameId && $style.gamePickItemSelected]"
                  @click="personalShareGameId = g.gameId"
                >
                  <div :class="$style.gamePickRow">
                    <span>Game {{ g.gameId }} — {{ g.hostUsername }} ({{ g.joinCode }})</span>
                    <button
                      type="button"
                      :class="$style.previewBtn"
                      @click.stop="viewSession(g)"
                    >
                      Preview
                    </button>
                  </div>
                </div>
              </div>
              <p v-else :class="$style.modalEmpty">{{ noShareableGamesMessage }}</p>
              <div v-if="personalShareError" :class="$style.personalShareError">{{ personalShareError }}</div>
              <div :class="$style.inlineShareActions">
                <button
                  :class="$style.primaryBtn"
                  :disabled="!personalShareEmail.trim() || !personalShareGameId || personalShareSending"
                  @click="shareGamePersonal"
                >
                  {{ personalShareSending ? "Sharing…" : "Share" }}
                </button>
              </div>
            </div>
          </div>

          <!-- Group game share content -->
          <div v-show="mainTab === 'groups'" :class="$style.tabPanel">
            <div :class="$style.card">
              <p :class="$style.cardDescription">Create a sharing group or join one with a code and password. Share games and invite other {{ collaboratorLabelPlural }}.</p>
              <div :class="$style.actionsRow">
                <button :class="$style.primaryBtn" @click="showCreateModal = true">Create group</button>
                <button :class="$style.primaryBtn" @click="showJoinModal = true">Join group</button>
              </div>
            </div>
            <div v-if="groups.length" :class="$style.section">
              <h2 :class="$style.sectionTitle">My groups</h2>
              <div :class="$style.groupList">
                <div v-for="g in groups" :key="g.id" :class="$style.groupCard">
                  <div :class="$style.groupCardContent">
                    <div :class="$style.groupName">{{ g.name }}</div>
                    <div v-if="g.description" :class="$style.groupCardDesc">{{ g.description }}</div>
                  </div>
                  <button :class="$style.viewBtn" @click="openGroup(g.id)">View</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Group detail: tabbed Shared games | Teachers -->
      <template v-else>
        <div v-if="groupLoading" :class="$style.loading">Loading...</div>
        <template v-else-if="group">
          <h1 :class="$style.pageTitle">{{ group.name }}</h1>
          <div :class="$style.groupActions">
            <button :class="$style.editDeleteBtn" @click="openEditGroupModal">Edit</button>
            <button :class="$style.editDeleteBtn" @click="openDeleteGroupModal">Delete</button>
          </div>
          <p v-if="group.description" :class="$style.groupDesc">{{ group.description }}</p>

          <!-- Tab headlines: Shared games | Teachers -->
          <div :class="$style.tabBar">
            <button
              :class="[$style.tab, activeGroupTab === 'games' && $style.tabActive]"
              @click="activeGroupTab = 'games'"
            >
              Shared games
            </button>
            <button
              :class="[$style.tab, activeGroupTab === 'collaborators' && $style.tabActive]"
              @click="activeGroupTab = 'collaborators'"
            >
              {{ collaboratorLabelPluralCapitalized }}
            </button>
          </div>

          <!-- Shared games content -->
          <div v-show="activeGroupTab === 'games'" :class="$style.tabPanel">
            <div :class="$style.panelCard">
              <div :class="$style.shareBtnWrap">
                <button :class="$style.primaryBtn" @click="showShareGameModal = true">Share a game</button>
                <button
                  v-if="sessions.length"
                  :class="[$style.primaryBtn, $style.filterBtn]"
                  @click="showFiltersPanel = true"
                >
                  Filter
                </button>
              </div>
              <div v-if="groupGamesLoading" :class="$style.loading">Loading games...</div>
              <template v-else>
                <div v-if="filteredSessions.length" :class="$style.sessionList">
                  <div
                    v-for="s in filteredSessions"
                    :key="s.gameId"
                    :class="$style.sessionRow"
                    @click="viewSession(s)"
                  >
                    <div :class="$style.sessionInfo">
                      <span :class="$style.sessionGameId">{{ (s.title && s.title.trim()) ? s.title.trim() : "Untitled" }}</span>
                      <span :class="$style.sessionMeta">{{ s.hostUsername }}</span>
                      <span v-if="s.sharedByUsername" :class="$style.sessionMeta">Shared by {{ s.sharedByUsername }}</span>
                      <div
                        v-if="s.shareGradeLevel || s.shareSubjectDiscipline"
                        :class="$style.sessionShareMetaRow"
                      >
                        <span v-if="s.shareGradeLevel" :class="$style.sessionMeta">
                          Grade {{ formatShareGradeLevel(s.shareGradeLevel) }}
                        </span>
                        <span
                          v-if="s.shareGradeLevel && s.shareSubjectDiscipline"
                          :class="$style.sessionMetaDivider"
                        >
                          |
                        </span>
                        <span v-if="s.shareSubjectDiscipline" :class="$style.sessionMeta">
                          Subject {{ formatShareSubjectDiscipline(s.shareSubjectDiscipline) }}
                        </span>
                      </div>
                    </div>
                    <button :class="$style.viewBtn" @click.stop="viewSession(s)">View</button>
                  </div>
                </div>
                <p v-else :class="$style.noGames">{{ sessions.length ? "No games match the filters." : "No games shared yet." }}</p>
              </template>
            </div>
          </div>

          <!-- Teachers content -->
          <div v-show="activeGroupTab === 'collaborators'" :class="$style.tabPanel">
            <div :class="$style.panelCard">
              <div :class="$style.teacherList">
                <div
                  v-for="(m, index) in group.members"
                  :key="m.id"
                  :class="$style.teacherItem"
                >
                  <div :class="$style.teacherInfo">
                    <span :class="$style.teacherName">{{ m.firstName }} {{ m.lastName }}</span>
                    <span v-if="m.email" :class="$style.teacherEmail">{{ m.email }}</span>
                  </div>
                  <span :class="[$style.teacherRole, index === 0 && $style.teacherRoleMain]">
                    {{ index === 0 ? mainCollaboratorLabel : associateCollaboratorLabel }}
                  </span>
                </div>
              </div>
              <div :class="$style.inviteBtnWrap">
                <button :class="$style.primaryBtn" @click="openInviteModal">Invite {{ collaboratorLabelPlural }}</button>
              </div>
              <p v-if="inviteMessage" :class="$style.inviteMessage">{{ inviteMessage }}</p>
            </div>
          </div>
        </template>
      </template>
    </div>

    <!-- Create group modal -->
    <Teleport to="body">
      <div v-if="showCreateModal" :class="$style.modalOverlay" @click.self="showCreateModal = false">
        <div :class="$style.modal" role="dialog" aria-modal="true">
          <h3 :class="$style.modalTitle">Create group</h3>
          <div :class="$style.formGroup">
            <label :class="$style.formLabel">Name</label>
            <input v-model="createForm.name" type="text" placeholder="Group name" :class="$style.formInput" />
          </div>
          <div :class="$style.formGroup">
            <label :class="$style.formLabel">Description</label>
            <textarea v-model="createForm.description" placeholder="Optional description" rows="3" :class="$style.formInput" />
          </div>
          <div :class="$style.formGroup">
            <label :class="$style.formLabel">Password</label>
            <input v-model="createForm.password" type="password" placeholder="Group password" :class="$style.formInput" />
          </div>
          <p v-if="createError" :class="$style.errorText">{{ createError }}</p>
          <div :class="$style.modalActions">
            <button :class="$style.secondaryBtn" @click="showCreateModal = false">Cancel</button>
            <button :class="$style.primaryBtn" :disabled="createSending" @click="createGroup">Create</button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Join group modal -->
    <Teleport to="body">
      <div v-if="showJoinModal" :class="$style.modalOverlay" @click.self="showJoinModal = false">
        <div :class="$style.modal" role="dialog" aria-modal="true">
          <h3 :class="$style.modalTitle">Join group</h3>
          <div :class="$style.formGroup">
            <label :class="$style.formLabel">Join code</label>
            <input v-model="joinForm.joinCode" type="text" placeholder="Enter code from inviter" :class="$style.formInput" />
          </div>
          <div :class="$style.formGroup">
            <label :class="$style.formLabel">Password</label>
            <input v-model="joinForm.password" type="password" placeholder="Group password" :class="$style.formInput" />
          </div>
          <p v-if="joinError" :class="$style.errorText">{{ joinError }}</p>
          <div :class="$style.modalActions">
            <button :class="$style.secondaryBtn" @click="showJoinModal = false">Cancel</button>
            <button :class="$style.primaryBtn" :disabled="joinSending" @click="joinGroup">Join</button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Share game modal: to group or to a person -->
    <Teleport to="body">
      <div v-if="showShareGameModal" :class="$style.modalOverlay" @click.self="closeShareGameModal">
        <div :class="[$style.modal, $style.shareModal]">
          <h3 :class="$style.modalTitle">Share a game</h3>
          <div :class="$style.shareModalBody">
            <div :class="$style.shareModeTabs">
              <button
                :class="[$style.shareModeTab, shareMode === 'group' && $style.shareModeTabActive]"
                @click="shareMode = 'group'"
              >
                To this group
              </button>
              <button
                :class="[$style.shareModeTab, shareMode === 'person' && $style.shareModeTabActive]"
                @click="shareMode = 'person'"
              >
                To a teacher
              </button>
            </div>
            <template v-if="shareMode === 'group'">
              <p :class="$style.modalHint">Choose the intended classroom context, select a game, then confirm with Share.</p>
              <div :class="$style.formGroup">
                <label :class="$style.formLabel">Grade level</label>
                <select v-model="groupShareMetadata.gradeLevel" :class="$style.formInput">
                  <option value="">Select a grade level</option>
                  <option
                    v-for="opt in shareGradeLevelOptions"
                    :key="opt.selectionValue"
                    :value="opt.selectionValue"
                  >
                    {{ opt.name }}
                  </option>
                </select>
              </div>
              <div :class="$style.formGroup">
                <label :class="$style.formLabel">Subject / discipline</label>
                <select v-model="groupShareMetadata.subjectDiscipline" :class="$style.formInput">
                  <option value="">Select a subject / discipline</option>
                  <option
                    v-for="opt in disciplineOptions"
                    :key="opt.selectionValue"
                    :value="opt.selectionValue"
                  >
                    {{ opt.label }}
                  </option>
                </select>
              </div>
              <div :class="$style.formGroup">
                <label :class="$style.formLabel">Learning standard (optional)</label>
                <textarea
                  v-model="groupShareMetadata.learningStandard"
                  rows="3"
                  placeholder="Add a standard code or description, if appropriate"
                  :class="$style.formInput"
                />
              </div>
              <p v-if="groupShareError" :class="$style.errorText">{{ groupShareError }}</p>
              <div v-if="myGamesForShare.length" :class="[$style.gamePickList, $style.shareModalGamePickList]">
                <div
                  v-for="g in myGamesForShare"
                  :key="g.gameId"
                  :class="[
                    $style.gamePickItem,
                    selectedGroupShareGameId === g.gameId && $style.gamePickItemSelected,
                    groupShareSending && $style.gamePickItemDisabled
                  ]"
                  @click="selectedGroupShareGameId = g.gameId"
                >
                  <div :class="$style.gamePickRow">
                    <span>Game {{ g.gameId }} — {{ g.hostUsername }} ({{ g.joinCode }})</span>
                    <button
                      type="button"
                      :class="$style.previewBtn"
                      @click.stop="viewSession(g)"
                    >
                      Preview
                    </button>
                  </div>
                </div>
              </div>
              <p v-else :class="$style.modalEmpty">{{ noShareableGamesMessage }}</p>
            </template>
            <template v-else>
              <p :class="$style.modalHint">Enter the recipient email, select a game, then confirm with Share.</p>
              <label :class="$style.personalShareLabel">Recipient email</label>
              <input
                v-model="personalShareEmail"
                type="email"
                placeholder="colleague@example.com"
                :class="$style.personalShareInput"
              />
              <div v-if="myGamesForShare.length" :class="$style.gamePickList">
                <div
                  v-for="g in myGamesForShare"
                  :key="g.gameId"
                  :class="[$style.gamePickItem, personalShareGameId === g.gameId && $style.gamePickItemSelected]"
                  @click="personalShareGameId = g.gameId"
                >
                  <div :class="$style.gamePickRow">
                    <span>Game {{ g.gameId }} — {{ g.hostUsername }} ({{ g.joinCode }})</span>
                    <button
                      type="button"
                      :class="$style.previewBtn"
                      @click.stop="viewSession(g)"
                    >
                      Preview
                    </button>
                  </div>
                </div>
              </div>
              <p v-else :class="$style.modalEmpty">No games to share.</p>
              <div v-if="personalShareError" :class="$style.personalShareError">{{ personalShareError }}</div>
            </template>
          </div>
          <div :class="$style.modalActionsSpread">
            <button :class="$style.secondaryBtn" @click="closeShareGameModal">Close</button>
            <button
              v-if="shareMode === 'group'"
              :class="$style.primaryBtn"
              :disabled="!selectedGroupShareGameId || groupShareSending"
              @click="shareGame"
            >
              {{ groupShareSending ? "Sharing…" : "Share" }}
            </button>
            <button
              v-else
              :class="$style.primaryBtn"
              :disabled="!personalShareEmail.trim() || !personalShareGameId || personalShareSending"
              @click="shareGamePersonal"
            >
              {{ personalShareSending ? "Sharing…" : "Share" }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Filter popup -->
    <Teleport to="body">
      <div v-if="showFiltersPanel" :class="$style.modalOverlay" @click.self="showFiltersPanel = false">
        <div :class="[$style.modal, $style.filterModal]" role="dialog" aria-modal="true">
          <h3 :class="$style.modalTitle">Filter games</h3>
          <div :class="$style.filtersBar">
            <div :class="$style.filterSection">
              <div :class="$style.filterSectionTitle">Host &amp; Game</div>
              <div :class="$style.filterRow">
                <label :class="$style.filterLabel">Host</label>
                <input v-model="gameFilters.host" type="text" placeholder="Host username" :class="$style.filterInput" />
                <label :class="$style.filterLabel">Join Code</label>
                <input v-model="gameFilters.joinCode" type="text" placeholder="Join code" :class="$style.filterInput" />
                <label :class="$style.filterLabel">Game ID</label>
                <input v-model.number="gameFilters.gameId" type="number" placeholder="Game ID" :class="$style.filterInput" />
              </div>
            </div>
            <div :class="$style.filterSection">
              <div :class="$style.filterSectionTitle">Discipline</div>
              <div :class="$style.filterRow">
                <label :class="$style.filterLabel">Discipline</label>
                <select v-model="gameFilters.discipline" :class="$style.filterSelect">
                  <option value="">All disciplines</option>
                  <option v-for="opt in disciplineOptions" :key="opt.selectionValue" :value="opt.selectionValue">{{ opt.label }}</option>
                </select>
              </div>
            </div>
            <div :class="$style.filterSection">
              <div :class="$style.filterSectionTitle">Date</div>
              <div :class="$style.filterRow">
                <div :class="$style.dateModeGroup">
                  <label :class="$style.radioLabel">
                    <input v-model="gameFilters.dateMode" type="radio" value="single" :class="$style.radioInput" />
                    Single date
                  </label>
                  <label :class="$style.radioLabel">
                    <input v-model="gameFilters.dateMode" type="radio" value="range" :class="$style.radioInput" />
                    Date range
                  </label>
                </div>
                <template v-if="gameFilters.dateMode === 'single'">
                  <label :class="$style.filterLabel">Date</label>
                  <input v-model="gameFilters.singleDate" type="date" :class="$style.filterInput" />
                </template>
                <template v-else>
                  <label :class="$style.filterLabel">From</label>
                  <input v-model="gameFilters.dateFrom" type="date" :class="$style.filterInput" />
                  <label :class="$style.filterLabel">To</label>
                  <input v-model="gameFilters.dateTo" type="date" :class="$style.filterInput" />
                </template>
              </div>
            </div>
            <div :class="$style.filterSection">
              <div :class="$style.filterSectionTitle">Filter by {{ collaboratorLabelPlural }}</div>
              <div :class="$style.filterRow">
                <label :class="$style.filterLabel">Show games shared by</label>
                <div :class="$style.teacherCheckboxGroup">
                  <label v-for="m in (group && group.members) || []" :key="m.id" :class="$style.teacherCheckboxLabel">
                    <input
                      v-model="gameFilters.teacherIds"
                      type="checkbox"
                      :value="m.id"
                      :class="$style.teacherCheckbox"
                    />
                    {{ m.firstName }} {{ m.lastName }}
                  </label>
                </div>
                <span v-if="gameFilters.teacherIds.length === 0" :class="$style.filterHint">All {{ collaboratorLabelPlural }} (default)</span>
              </div>
            </div>
          </div>
          <div :class="$style.modalActions">
            <button :class="$style.clearFiltersBtn" @click="clearGameFilters">Clear</button>
            <button :class="$style.secondaryBtn" @click="showFiltersPanel = false">Cancel</button>
            <button :class="$style.primaryBtn" @click="showFiltersPanel = false">Apply</button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Invite collaborators modal: general info then review email -->
    <Teleport to="body">
      <div v-if="showInviteModal" :class="$style.modalOverlay" @click.self="closeInviteModal">
        <div :class="$style.modal" role="dialog" aria-modal="true">
          <h3 :class="$style.modalTitle">{{ inviteModalTitle }}</h3>

          <template v-if="inviteStep === 'info'">
            <div :class="$style.inviteGeneralInfo">
              <p :class="$style.inviteInfoText">Invite a {{ collaboratorLabelSingular }} to join <strong>{{ group?.name }}</strong>. They will need the join code and password below to join the group.</p>
              <div :class="$style.inviteInfoRow">
                <span :class="$style.inviteInfoLabel">Join code:</span>
                <span :class="$style.inviteInfoValue">{{ group?.joinCode || "—" }}</span>
              </div>
              <div :class="$style.inviteInfoRow" v-if="storedGroupPassword">
                <span :class="$style.inviteInfoLabel">Group password:</span>
                <span :class="$style.inviteInfoValue">{{ storedGroupPassword }}</span>
              </div>
              <div :class="$style.formGroup" v-else>
                <label :class="$style.formLabel">Group password (enter to include in email)</label>
                <input v-model="inviteForm.password" type="text" placeholder="Enter group password" :class="$style.formInput" />
              </div>
            </div>
            <div :class="$style.modalActions">
              <button :class="$style.secondaryBtn" @click="closeInviteModal">Cancel</button>
              <button :class="$style.primaryBtn" @click="goToReviewEmail">Review email</button>
            </div>
          </template>

          <template v-else>
            <div :class="$style.formGroup">
              <label :class="$style.formLabel">To</label>
              <div :class="$style.emailChipContainer" @click="focusEmailInput">
                <span
                  v-for="(em, idx) in inviteForm.emails"
                  :key="idx"
                  :class="$style.emailChip"
                  :title="em"
                >
                  {{ truncateEmail(em) }}
                  <button type="button" :class="$style.chipRemove" aria-label="Remove" @click.stop="removeEmailChip(idx)">&times;</button>
                </span>
                <input
                  ref="emailInputRef"
                  v-model="inviteForm.emailInput"
                  type="text"
                  placeholder="Type email and press Enter"
                  :class="$style.emailChipInput"
                  @keydown.enter.prevent="addEmailChip"
                  @keydown="onEmailKeydown"
                />
              </div>
            </div>
            <div :class="$style.formGroup">
              <label :class="$style.formLabel">Email content</label>
              <textarea v-model="inviteForm.body" rows="8" placeholder="Email body" :class="$style.formInput" />
            </div>
            <p v-if="inviteError" :class="$style.errorText">{{ inviteError }}</p>
            <div :class="$style.modalActions">
              <button :class="$style.secondaryBtn" @click="inviteStep = 'info'">Back</button>
              <button :class="$style.primaryBtn" :disabled="inviteSending" @click="sendInvite">Send invite</button>
            </div>
          </template>
        </div>
      </div>
    </Teleport>

    <!-- Edit group modal -->
    <Teleport to="body">
      <div v-if="showEditGroupModal" :class="$style.modalOverlay" @click.self="showEditGroupModal = false">
        <div :class="$style.modal" role="dialog" aria-modal="true">
          <h3 :class="$style.modalTitle">Edit group</h3>
          <div :class="$style.formGroup">
            <label :class="$style.formLabel">Group name</label>
            <input v-model="editGroupForm.name" type="text" placeholder="Group name" :class="$style.formInput" />
          </div>
          <p v-if="editGroupError" :class="$style.errorText">{{ editGroupError }}</p>
          <div :class="$style.modalActions">
            <button :class="$style.secondaryBtn" @click="showEditGroupModal = false">Cancel</button>
            <button :class="$style.primaryBtn" :disabled="editGroupSaving" @click="saveEditGroup">Save</button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Delete group modal: must type group name to confirm -->
    <Teleport to="body">
      <div v-if="showDeleteGroupModal" :class="$style.modalOverlay" @click.self="closeDeleteGroupModal">
        <div :class="$style.modal" role="dialog" aria-modal="true">
          <h3 :class="$style.modalTitle">Delete group</h3>
          <p :class="$style.deleteConfirmText">To delete this group, enter the group name below:</p>
          <p :class="$style.deleteGroupNameRef">{{ group?.name }}</p>
          <div :class="$style.formGroup">
            <label :class="$style.formLabel">Group name</label>
            <input v-model="deleteConfirmName" type="text" placeholder="Type group name to confirm" :class="$style.formInput" />
          </div>
          <p v-if="deleteGroupError" :class="$style.errorText">{{ deleteGroupError }}</p>
          <div :class="$style.modalActions">
            <button :class="$style.secondaryBtn" @click="closeDeleteGroupModal">Cancel</button>
            <button
              :class="$style.primaryBtn"
              :disabled="deleteGroupSending || !isDeleteConfirmMatch"
              @click="confirmDeleteGroup"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <SessionDetailModal
      :showModal="!!sessionForDetail"
      :session="sessionForDetail"
      :closeModal="() => (sessionForDetail = null)"
      :viewerRole="viewerRole"
      :accessScope="sharedSessionAccessScope"
    />
  </div>
</template>

<script>
import { storeToRefs } from "pinia";
import globalStyles from "@/css/global.module.css";
import SaboteurLogo from "../assets/saboteur-logo.svg";
import HeaderActionButton from "@/components/buttons/HeaderActionButton.vue";
import LeftArrowIcon from "@/assets/icons/left-arrow.svg";
import SessionDetailModal from "@/components/util/SessionDetailModal.vue";
import HeaderLanguageSelector from "@/components/util/HeaderLanguageSelector.vue";
import gameService from "@/services/game.service";
import { useAuthStore } from "@/stores/authStore";
import { toast } from "vue3-toastify";
import { disciplineOptions, sciArgTargetAudienceOptions } from "@/utils/data";
import { APP_ROUTE_NAMES, USER_ROLES } from "@/utils/permissions";
import { SESSION_VIEW_PRESETS, createSessionFilterState, getSessionStartDateKey } from "@/utils/sessionViews";
// eslint-disable-next-line no-unused-vars -- decryptGroupPassword used in openInviteModal
import { encryptGroupPassword, decryptGroupPassword, persistGroupPasswords, loadGroupPasswords } from "@/utils/groupPasswordCrypto";

export default {
  name: "TeacherGroupsView",
  components: { HeaderActionButton, HeaderLanguageSelector, SessionDetailModal },
  data() {
    return {
      saboteurLogo: SaboteurLogo,
      leftArrowIcon: LeftArrowIcon,
      disciplineOptions,
      shareGradeLevelOptions: sciArgTargetAudienceOptions,
      groups: [],
      groupId: null,
      group: null,
      groupLoading: false,
      sessions: [],
      groupGamesLoading: false,
      showCreateModal: false,
      showJoinModal: false,
      createForm: { name: "", description: "", password: "" },
      createError: "",
      createSending: false,
      joinForm: { joinCode: "", password: "" },
      joinError: "",
      joinSending: false,
      mainTab: "sharedWithMe", // "sharedWithMe" | "groups"
      personalSubTab: "sharedToMe", // "sharedToMe" | "shareToOthers" (under Personal game share)
      sharedWithMeSessions: [],
      sharedWithMeLoading: false,
      sharedWithMeError: "",
      showShareGameModal: false,
      shareMode: "group", // "group" | "person"
      selectedGroupShareGameId: null,
      groupShareMetadata: {
        gradeLevel: "",
        subjectDiscipline: "",
        learningStandard: "",
      },
      groupShareError: "",
      groupShareSending: false,
      personalShareEmail: "",
      personalShareGameId: null,
      personalShareError: "",
      personalShareSending: false,
      myGamesForShare: [],
      inviteEmail: "",
      inviteSending: false,
      inviteMessage: "",
      sessionForDetail: null,
      activeGroupTab: "games", // "games" | "collaborators"
      showInviteModal: false,
      inviteStep: "info", // "info" | "review"
      inviteForm: { emails: [], emailInput: "", password: "", body: "" },
      inviteError: "",
      groupPasswords: {}, // groupId -> encrypted password (AES-GCM, base64)
      decryptedGroupPassword: "", // decrypted only when needed for display/API
      showEditGroupModal: false,
      editGroupForm: { name: "" },
      editGroupSaving: false,
      editGroupError: "",
      showDeleteGroupModal: false,
      deleteConfirmName: "",
      deleteGroupSending: false,
      deleteGroupError: "",
      gameFilters: createSessionFilterState({ includeTeacherIds: true }),
      showFiltersPanel: false,
    };
  },
  computed: {
    $globalStyle() {
      return globalStyles;
    },
    storedGroupPassword() {
      return this.decryptedGroupPassword || "";
    },
    effectiveInvitePassword() {
      return this.storedGroupPassword || (this.inviteForm.password || "").trim();
    },
    viewerRole() {
      return this.$route.meta.viewerRole || USER_ROLES.TEACHER;
    },
    roleLabel() {
      return this.viewerRole === USER_ROLES.RESEARCHER ? "Researcher" : "Teacher";
    },
    dashboardRouteName() {
      return this.viewerRole === USER_ROLES.RESEARCHER ? APP_ROUTE_NAMES.RESEARCHER : APP_ROUTE_NAMES.TEACHER;
    },
    groupsRouteName() {
      return this.viewerRole === USER_ROLES.RESEARCHER ? APP_ROUTE_NAMES.RESEARCHER_GROUPS : APP_ROUTE_NAMES.TEACHER_GROUPS;
    },
    groupDetailRouteName() {
      return this.viewerRole === USER_ROLES.RESEARCHER ? APP_ROUTE_NAMES.RESEARCHER_GROUP_DETAIL : APP_ROUTE_NAMES.TEACHER_GROUP_DETAIL;
    },
    collaboratorLabelSingular() {
      return this.viewerRole === USER_ROLES.RESEARCHER ? "researcher" : "teacher";
    },
    collaboratorLabelPlural() {
      return this.viewerRole === USER_ROLES.RESEARCHER ? "researchers" : "teachers";
    },
    collaboratorLabelPluralCapitalized() {
      return this.collaboratorLabelPlural.charAt(0).toUpperCase() + this.collaboratorLabelPlural.slice(1);
    },
    inviteModalTitle() {
      return this.inviteStep === "review" ? "Review & send invite" : `Invite ${this.collaboratorLabelPlural}`;
    },
    mainCollaboratorLabel() {
      return this.viewerRole === USER_ROLES.RESEARCHER ? "Main researcher" : "Main teacher";
    },
    associateCollaboratorLabel() {
      return this.viewerRole === USER_ROLES.RESEARCHER ? "Associate researcher" : "Associate teacher";
    },
    noShareableGamesMessage() {
      if (this.viewerRole === USER_ROLES.RESEARCHER) return "No public games available to share yet.";
      return "No games to share. Create games from the Teacher dashboard first.";
    },
    sharedSessionAccessScope() {
      return SESSION_VIEW_PRESETS.TEACHER_SHARED;
    },
    isDeleteConfirmMatch() {
      return (this.deleteConfirmName || "").trim() === (this.group?.name || "");
    },
    filteredSessions() {
      let list = this.sessions || [];
      const f = this.gameFilters;
      if (f.host && f.host.trim()) {
        const q = f.host.trim().toLowerCase();
        list = list.filter((s) => (s.hostUsername || "").toLowerCase().includes(q));
      }
      if (f.joinCode && f.joinCode.trim()) {
        const q = f.joinCode.trim();
        list = list.filter((s) => (s.joinCode || "").toString().includes(q));
      }
      if (f.gameId) {
        list = list.filter((s) => Number(s.gameId) === Number(f.gameId));
      }
      if (f.discipline && f.discipline.trim()) {
        list = list.filter((s) => (s.discipline || "") === f.discipline.trim());
      }
      if (f.dateMode === "single" && f.singleDate) {
        list = list.filter((s) => getSessionStartDateKey(s.startTime) === f.singleDate);
      } else if (f.dateMode === "range" && (f.dateFrom || f.dateTo)) {
        list = list.filter((s) => {
          const start = getSessionStartDateKey(s.startTime);
          if (f.dateFrom && start < f.dateFrom) return false;
          if (f.dateTo && start > f.dateTo) return false;
          return true;
        });
      }
      if (f.teacherIds && f.teacherIds.length > 0) {
        const ids = new Set(f.teacherIds.map((id) => Number(id)));
        list = list.filter((s) => s.sharedByUserId != null && ids.has(Number(s.sharedByUserId)));
      }
      return list;
    },
  },
  watch: {
    "$route.params.id": {
      handler(id) {
        this.groupId = id ? Number(id) : null;
        if (this.groupId) this.loadGroupDetail();
      },
      immediate: true,
    },
    showShareGameModal(v) {
      if (v) {
        this.loadMyGamesForShare();
        this.shareMode = "group";
        this.resetGroupShareMetadata();
        this.personalShareEmail = "";
        this.personalShareGameId = null;
        this.personalShareError = "";
      }
    },
  },
  async mounted() {
    try {
      const res = await gameService.sharingGroupsAvailable();
      if (res?.available !== true) {
        this.$router.replace({ name: this.dashboardRouteName });
        return;
      }
    } catch {
      this.$router.replace({ name: this.dashboardRouteName });
      return;
    }
    const loaded = loadGroupPasswords();
    this.groupPasswords = { ...loaded, ...this.groupPasswords };
    this.loadGroups();
    if (this.mainTab === "sharedWithMe") this.loadSharedWithMe();
  },
  methods: {
    formatShareGradeLevel(value) {
      if (!value) return "";
      const opt = this.shareGradeLevelOptions.find((option) => option.selectionValue === value);
      return opt ? opt.name : value;
    },
    formatShareSubjectDiscipline(value) {
      if (!value) return "";
      const opt = this.disciplineOptions.find((option) => option.selectionValue === value);
      return opt ? opt.label : value;
    },
    goBack() {
      if (this.groupId) this.$router.push({ name: this.groupsRouteName });
      else this.$router.push({ name: this.dashboardRouteName });
    },
    async loadGroups() {
      try {
        const res = await gameService.getMySharingGroups(this.viewerRole);
        if (res?.success && Array.isArray(res.groups)) this.groups = res.groups;
      } catch (e) {
        toast.error(e?.message || "Failed to load groups");
      }
    },
    openGroup(id) {
      this.$router.push({ name: this.groupDetailRouteName, params: { id: String(id) } });
    },
    async loadGroupDetail() {
      if (!this.groupId) return;
      this.groupLoading = true;
      this.group = null;
      try {
        const res = await gameService.getSharingGroup(this.groupId, this.viewerRole);
        if (res?.success) this.group = res.group;
        await this.loadGroupGames();
      } catch (e) {
        toast.error(e?.message || "Failed to load group");
      } finally {
        this.groupLoading = false;
      }
    },
    async loadGroupGames() {
      if (!this.groupId) return;
      this.groupGamesLoading = true;
      try {
        const res = await gameService.getSharingGroupGames(this.groupId, this.viewerRole);
        if (res?.success && Array.isArray(res.sessions)) this.sessions = res.sessions;
      } catch (e) {
        this.sessions = [];
      } finally {
        this.groupGamesLoading = false;
      }
    },
    applyGameFilters() {
      // Filtering is reactive via filteredSessions computed
    },
    clearGameFilters() {
      this.gameFilters = createSessionFilterState({ includeTeacherIds: true });
    },
    async createGroup() {
      this.createError = "";
      if (!this.createForm.name.trim()) {
        this.createError = "Name is required.";
        return;
      }
      if (!this.createForm.password.trim()) {
        this.createError = "Password is required.";
        return;
      }
      this.createSending = true;
      try {
        const res = await gameService.createSharingGroup({
          name: this.createForm.name.trim(),
          description: this.createForm.description.trim(),
          password: this.createForm.password,
        }, this.viewerRole);
        if (res?.success) {
          if (res.group?.id) {
            const encrypted = await encryptGroupPassword(this.createForm.password);
            this.groupPasswords[res.group.id] = encrypted;
            persistGroupPasswords(this.groupPasswords);
          }
          toast.success("Group created. Share the join code: " + res.group?.joinCode);
          this.showCreateModal = false;
          this.createForm = { name: "", description: "", password: "" };
          await this.loadGroups();
        } else {
          this.createError = res?.message || "Failed to create group";
        }
      } catch (e) {
        this.createError = e?.message || "Failed to create group";
      } finally {
        this.createSending = false;
      }
    },
    async joinGroup() {
      this.joinError = "";
      if (!this.joinForm.joinCode.trim()) {
        this.joinError = "Join code is required.";
        return;
      }
      if (!this.joinForm.password.trim()) {
        this.joinError = "Password is required.";
        return;
      }
      this.joinSending = true;
      try {
        const res = await gameService.joinSharingGroup({
          joinCode: this.joinForm.joinCode.trim().toUpperCase(),
          password: this.joinForm.password,
        }, this.viewerRole);
        if (res?.success) {
          if (res?.group?.id) {
            const encrypted = await encryptGroupPassword(this.joinForm.password);
            this.groupPasswords[res.group.id] = encrypted;
            persistGroupPasswords(this.groupPasswords);
          }
          toast.success(res?.group?.message || "Joined group.");
          this.showJoinModal = false;
          this.joinForm = { joinCode: "", password: "" };
          await this.loadGroups();
          if (res?.group?.id) this.openGroup(res.group.id);
        } else {
          this.joinError = res?.message || "Failed to join";
        }
      } catch (e) {
        this.joinError = e?.message || e?.error || "Failed to join";
      } finally {
        this.joinSending = false;
      }
    },
    openEditGroupModal() {
      this.editGroupForm.name = this.group?.name || "";
      this.editGroupError = "";
      this.showEditGroupModal = true;
    },
    async saveEditGroup() {
      const name = (this.editGroupForm.name || "").trim();
      if (!name) {
        this.editGroupError = "Group name is required.";
        return;
      }
      this.editGroupError = "";
      this.editGroupSaving = true;
      try {
        await gameService.updateSharingGroup(this.groupId, { name }, this.viewerRole);
        this.group.name = name;
        this.showEditGroupModal = false;
        toast.success("Group updated.");
      } catch (e) {
        this.editGroupError = e?.message || "Failed to update group";
      } finally {
        this.editGroupSaving = false;
      }
    },
    openDeleteGroupModal() {
      this.deleteConfirmName = "";
      this.deleteGroupError = "";
      this.showDeleteGroupModal = true;
    },
    closeDeleteGroupModal() {
      this.showDeleteGroupModal = false;
      this.deleteConfirmName = "";
      this.deleteGroupError = "";
    },
    async confirmDeleteGroup() {
      if (!this.isDeleteConfirmMatch) return;
      this.deleteGroupError = "";
      this.deleteGroupSending = true;
      try {
        await gameService.deleteSharingGroup(this.groupId, this.viewerRole);
        delete this.groupPasswords[this.groupId];
        persistGroupPasswords(this.groupPasswords);
        this.closeDeleteGroupModal();
        toast.success("Group deleted.");
        this.$router.push({ name: this.groupsRouteName });
        this.loadGroups();
      } catch (e) {
        this.deleteGroupError = e?.message || "Failed to delete group";
      } finally {
        this.deleteGroupSending = false;
      }
    },
    async     openInviteModal() {
      this.inviteStep = "info";
      this.inviteForm = { emails: [], emailInput: "", password: "", body: "" };
      this.inviteError = "";
      this.decryptedGroupPassword = "";
      const encrypted = this.groupPasswords[this.groupId];
      if (encrypted) {
        this.decryptedGroupPassword = await decryptGroupPassword(encrypted);
      } else {
        this.inviteForm.password = "";
      }
      this.showInviteModal = true;
    },
    closeInviteModal() {
      this.showInviteModal = false;
      this.inviteStep = "info";
      this.inviteForm = { emails: [], emailInput: "", password: "", body: "" };
      this.inviteError = "";
      this.decryptedGroupPassword = "";
    },
    focusEmailInput() {
      this.$nextTick(() => this.$refs.emailInputRef?.focus());
    },
    onEmailKeydown(e) {
      if (e.key === ",") {
        e.preventDefault();
        this.addEmailChip();
      }
    },
    addEmailChip() {
      const raw = (this.inviteForm.emailInput || "").trim();
      if (!raw) return;
      const email = raw.replace(/,\s*$/, "").trim();
      if (!email || !/^[^@]+@[^@]+\.[^@]+$/.test(email)) {
        this.inviteError = "Enter a valid email address.";
        return;
      }
      if (this.inviteForm.emails.includes(email)) return;
      this.inviteForm.emails.push(email);
      this.inviteForm.emailInput = "";
      this.inviteError = "";
    },
    truncateEmail(email) {
      if (!email || typeof email !== "string") return email || "";
      const at = email.indexOf("@");
      if (at <= 0) return email;
      const local = email.slice(0, at);
      const domain = email.slice(at);
      const maxLocal = 12;
      if (local.length <= maxLocal) return email;
      return local.slice(0, maxLocal) + "..." + domain;
    },
    removeEmailChip(idx) {
      this.inviteForm.emails.splice(idx, 1);
    },
    goToReviewEmail() {
      const g = this.group;
      const joinCode = g?.joinCode || "";
      const pwd = this.effectiveInvitePassword;
      const pwdLine = pwd ? `Password: ${pwd}` : "Password: (ask the person who invited you for the group password)";
      const desc = g?.description || "No description.";
      this.inviteForm.body = `You have been invited to join the sharing group "${g?.name || ""}" on Saboteur.

To join, go to the ${this.roleLabel} dashboard and choose "Join group". Enter:
- Join code: ${joinCode}
- ${pwdLine}

Group description: ${desc}
`;
      this.inviteStep = "review";
      this.inviteError = "";
    },
    async sendInvite() {
      let emails = [...this.inviteForm.emails];
      const pending = (this.inviteForm.emailInput || "").trim();
      if (pending && /^[^@]+@[^@]+\.[^@]+$/.test(pending) && !emails.includes(pending)) {
        emails.push(pending);
      }
      if (!emails.length) {
        this.inviteError = "Add at least one email address (type and press Enter).";
        return;
      }
      this.inviteError = "";
      this.inviteSending = true;
      const pwd = this.effectiveInvitePassword;
      const body = (this.inviteForm.body || "").trim() || undefined;
      const sent = [];
      const failed = [];
      try {
        for (const email of emails) {
          try {
            await gameService.inviteToSharingGroup(this.groupId, {
              email,
              password: pwd || undefined,
              body,
            }, this.viewerRole);
            sent.push(email);
          } catch (e) {
            failed.push({ email, err: e?.message || "Failed" });
          }
        }
        if (pwd && !this.groupPasswords[this.groupId] && sent.length) {
          const encrypted = await encryptGroupPassword(pwd);
          this.groupPasswords[this.groupId] = encrypted;
          persistGroupPasswords(this.groupPasswords);
        }
        if (failed.length) {
          this.inviteError = "Failed for: " + failed.map((f) => f.email).join(", ");
        } else {
          const msg = "Invitation sent to " + (sent.length > 1 ? sent.join(", ") : sent[0]);
          this.inviteMessage = msg;
          toast.success(msg);
          this.closeInviteModal();
        }
      } finally {
        this.inviteSending = false;
      }
    },
    viewSession(session) {
      this.sessionForDetail = session;
    },
    async loadSharedWithMe() {
      this.sharedWithMeLoading = true;
      this.sharedWithMeError = "";
      try {
        const res = await gameService.getSharedWithMe(this.viewerRole);
        if (res && res.success && Array.isArray(res.sessions)) {
          this.sharedWithMeSessions = res.sessions;
        } else {
          this.sharedWithMeSessions = [];
          this.sharedWithMeError = res?.message || "Failed to load.";
        }
      } catch (e) {
        this.sharedWithMeSessions = [];
        this.sharedWithMeError = e?.message || "Failed to load shared games.";
      } finally {
        this.sharedWithMeLoading = false;
      }
    },
    async loadMyGamesForShare() {
      try {
        const res = this.viewerRole === USER_ROLES.RESEARCHER
          ? await gameService.getPublicSessions()
          : await gameService.getMySessions();
        if (res?.data?.success && Array.isArray(res.data.sessions)) {
          this.myGamesForShare = res.data.sessions;
        } else {
          this.myGamesForShare = [];
        }
      } catch (e) {
        this.myGamesForShare = [];
      }
    },
    resetGroupShareMetadata() {
      this.groupShareMetadata = {
        gradeLevel: "",
        subjectDiscipline: "",
        learningStandard: "",
      };
      this.selectedGroupShareGameId = null;
      this.groupShareError = "";
      this.groupShareSending = false;
    },
    closeShareGameModal() {
      this.showShareGameModal = false;
      this.resetGroupShareMetadata();
      this.personalShareError = "";
    },
    async shareGame() {
      const gameId = this.selectedGroupShareGameId;
      const gradeLevel = (this.groupShareMetadata.gradeLevel || "").trim();
      const subjectDiscipline = (this.groupShareMetadata.subjectDiscipline || "").trim();
      const learningStandard = (this.groupShareMetadata.learningStandard || "").trim();
      if (!gameId) {
        this.groupShareError = "Please select a game to share.";
        return;
      }
      if (!gradeLevel || !subjectDiscipline) {
        this.groupShareError = "Grade level and subject / discipline are required for group sharing.";
        return;
      }
      if (this.groupShareSending) return;
      this.groupShareError = "";
      this.groupShareSending = true;
      try {
        const res = await gameService.shareGameToGroup(
          this.groupId,
          gameId,
          {
            gradeLevel,
            subjectDiscipline,
            learningStandard,
          },
          this.viewerRole
        );
        const message = res?.message || "Game shared.";
        toast.success(message);
        this.showShareGameModal = false;
        await this.loadGroupGames();
        if (this.group && /shared\./i.test(message)) {
          this.group.gameCount = (this.group.gameCount || 0) + 1;
        }
      } catch (e) {
        const msg = e?.message || "Failed to share game";
        this.groupShareError = msg;
        toast.error(msg);
      } finally {
        this.groupShareSending = false;
      }
    },
    async shareGamePersonal() {
      const email = (this.personalShareEmail || "").trim();
      const gameId = this.personalShareGameId;
      if (!email || !gameId) return;
      this.personalShareError = "";
      this.personalShareSending = true;
      try {
        await gameService.shareGamePersonal(gameId, email);
        toast.success("Game shared with " + email);
        this.personalShareEmail = "";
        this.personalShareGameId = null;
      } catch (e) {
        const msg = e?.message || e?.error || "Failed to share";
        this.personalShareError = msg;
        toast.error(msg);
      } finally {
        this.personalShareSending = false;
      }
    },
  },
  setup() {
    const authStore = useAuthStore();
    const { user } = storeToRefs(authStore);

    return {
      authStore,
      user,
    };
  },
};
</script>

<style module>
.page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}
.bodyContainer {
  flex: 1;
  padding: 40px 20px;
  max-width: 640px;
  margin: 0 auto;
  width: 100%;
}
.shareGameWrap {
  width: 100%;
  max-width: 720px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.shareGameWrap .pageTitle {
  margin-bottom: 32px;
}
.shareGameChoiceRow {
  display: flex;
  align-items: stretch;
  justify-content: center;
  gap: 0;
  width: 100%;
  margin-bottom: 32px;
}
.shareGameChoiceCard {
  flex: 1;
  max-width: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 28px 24px;
  background: #fff;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
  font-family: "Poppins", sans-serif;
  text-align: center;
  min-height: 100px;
}
.shareGameChoiceCard:hover {
  border-color: #3f72af;
  background: #f8fafd;
  box-shadow: 0 2px 12px rgba(63, 114, 175, 0.15);
}
.shareGameChoiceCardActive {
  border-color: #3f72af;
  background: #e8eef5;
  box-shadow: 0 2px 12px rgba(63, 114, 175, 0.2);
}
.shareGameChoiceLabel {
  font-size: 18px;
  font-weight: 600;
  color: #112d4e;
  display: block;
  margin-bottom: 8px;
}
.shareGameChoiceDesc {
  font-size: 14px;
  color: #666;
  line-height: 1.3;
}
.shareGameDivider {
  display: flex;
  align-items: center;
  padding: 0 16px;
  font-size: 24px;
  font-weight: 300;
  color: #bbb;
  user-select: none;
}
.personalSubChoiceRow {
  display: flex;
  align-items: stretch;
  justify-content: center;
  gap: 0;
  width: 100%;
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: 1px solid #d0d0d0;
}
.personalSubChoiceCard {
  flex: 1;
  width: 50%;
  padding: 10px 18px;
  background: #e8e8e8;
  border: 1px solid #d0d0d0;
  border-bottom: none;
  border-radius: 6px 6px 0 0;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  font-family: "Poppins", sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #555;
  text-align: center;
  margin-bottom: -1px;
}
.personalSubChoiceCard:first-child {
  border-right: none;
  border-radius: 6px 0 0 0;
}
.personalSubChoiceCard:last-child {
  border-left: 1px solid #d0d0d0;
  border-radius: 0 6px 0 0;
}
.personalSubChoiceCard:hover {
  background: #ddd;
  color: #333;
}
.personalSubChoiceCardActive {
  background: #fff;
  color: #333;
  border-bottom: 1px solid #fff;
  font-weight: 600;
}
.shareGameWrap .tabPanel {
  width: 100%;
}
.pageTitle {
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 32px;
  color: #333;
  margin-bottom: 8px;
  text-align: center;
}
.groupActions {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 16px;
}
.editDeleteBtn {
  padding: 8px 18px;
  font-family: "Poppins", sans-serif;
  font-size: 14px;
  font-weight: 500;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid #ccc;
  background: #fff;
  color: #333;
}
.editDeleteBtn:hover {
  background: #f5f5f5;
  border-color: #999;
}
.editDeleteBtn:first-child {
  border-color: #3f72af;
  color: #3f72af;
}
.editDeleteBtn:first-child:hover {
  background: #e8eef5;
}
.editDeleteBtn:last-child {
  border-color: #c62828;
  color: #c62828;
}
.editDeleteBtn:last-child:hover {
  background: #ffebee;
}
.card {
  background: #fff;
  border-radius: 12px;
  padding: 28px 32px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  margin-bottom: 24px;
  border: 1px solid #e8e8e8;
}
.cardDescription {
  font-family: "Poppins", sans-serif;
  font-size: 15px;
  color: #555;
  line-height: 1.5;
  margin-bottom: 24px;
  text-align: center;
}
.groupDesc {
  font-size: 16px;
  color: #666;
  margin-bottom: 24px;
  text-align: center;
}
.actionsRow {
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
}
.actionsRow .primaryBtn {
  min-width: 280px;
}
.primaryBtn {
  padding: 12px 24px;
  background: #3f72af;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  font-family: "Poppins", sans-serif;
  font-size: 15px;
}
.primaryBtn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.secondaryBtn {
  padding: 12px 24px;
  background: #e8e8e8;
  color: #333;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  font-family: "Poppins", sans-serif;
  font-size: 15px;
}
.secondaryBtn:hover {
  background: #ddd;
}
.section {
  margin-top: 32px;
}
.sectionTitle {
  font-family: "Poppins", sans-serif;
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
}
.groupList {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.groupCard {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  padding: 18px 20px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.groupCardContent {
  flex: 1;
  min-width: 0;
}
.groupName {
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 18px;
  color: #112d4e;
}
.groupCardDesc {
  font-size: 14px;
  color: #666;
  margin-top: 6px;
  line-height: 1.4;
}
.viewBtn {
  flex-shrink: 0;
  padding: 10px 20px;
  background: #3f72af;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  font-family: "Poppins", sans-serif;
  cursor: pointer;
}
.viewBtn:hover {
  background: #2d5a8f;
}
/* Main tabs: Shared with me | My groups */
.mainTabBar {
  display: flex;
  gap: 0;
  margin-top: 20px;
  margin-bottom: 0;
  border-bottom: 2px solid #e0e0e0;
}
.mainTab {
  padding: 14px 28px;
  font-family: "Poppins", sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #666;
  background: transparent;
  border: none;
  border-bottom: 3px solid transparent;
  margin-bottom: -2px;
  cursor: pointer;
  transition: color 0.2s, border-color 0.2s;
}
.mainTab:hover {
  color: #333;
}
.mainTabActive {
  color: #3f72af;
  border-bottom-color: #3f72af;
}
/* Tab bar: Shared games | Teachers (inside a group) */
.tabBar {
  display: flex;
  gap: 0;
  justify-content: center;
  margin-top: 24px;
  margin-bottom: 0;
  border-bottom: 2px solid #e0e0e0;
}
.tab {
  padding: 14px 28px;
  font-family: "Poppins", sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #666;
  background: transparent;
  border: none;
  border-bottom: 3px solid transparent;
  margin-bottom: -2px;
  cursor: pointer;
  transition: color 0.2s, border-color 0.2s;
}
.tab:hover {
  color: #333;
}
.tabActive {
  color: #3f72af;
  border-bottom-color: #3f72af;
}
.tabPanel {
  margin-top: 0;
}
.panelCard {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  padding: 24px;
  margin-top: 20px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}
.shareBtnWrap {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}
.filterBtn {
  background-color: #5a6c7d;
}
.filterBtn:hover {
  background-color: #4a5c6d;
}

.filterModal .filtersBar {
  margin-top: 0;
  margin-bottom: 0;
  max-height: 60vh;
  overflow-y: auto;
}

/* Filters (admin-style + filter by teachers) */
.filtersBar {
  margin-top: 20px;
  margin-bottom: 20px;
  padding: 20px;
  background: #f5f5f5;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}
.filterSection {
  margin-bottom: 20px;
}
.filterSection:last-of-type {
  margin-bottom: 0;
}
.filterSectionTitle {
  font-family: "Poppins", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
  padding-bottom: 6px;
  border-bottom: 1px solid #e0e0e0;
}
.filterRow {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}
.dateModeGroup {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-right: 8px;
}
.radioLabel {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: "Poppins", sans-serif;
  font-size: 14px;
  color: #333;
  cursor: pointer;
}
.radioInput {
  margin: 0;
  cursor: pointer;
}
.filterActions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
}
.filterLabel {
  font-family: "Poppins", sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #333;
  min-width: 70px;
}
.filterInput {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-family: "Poppins", sans-serif;
  font-size: 14px;
  min-width: 120px;
}
.filterSelect {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-family: "Poppins", sans-serif;
  font-size: 14px;
  min-width: 140px;
  background: #fff;
  cursor: pointer;
}
.applyFiltersBtn,
.clearFiltersBtn {
  padding: 8px 20px;
  border: none;
  border-radius: 6px;
  font-family: "Poppins", sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}
.applyFiltersBtn {
  background-color: #3f72af;
  color: white;
}
.applyFiltersBtn:hover {
  background-color: #5a8fc7;
}
.clearFiltersBtn {
  background-color: #fff;
  color: #666;
  border: 1px solid #ccc;
}
.clearFiltersBtn:hover {
  background-color: #f0f0f0;
}
.teacherCheckboxGroup {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 24px;
  align-items: center;
}
.teacherCheckboxLabel {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: "Poppins", sans-serif;
  font-size: 14px;
  color: #333;
  cursor: pointer;
}
.teacherCheckbox {
  margin: 0;
  cursor: pointer;
}
.filterHint {
  font-size: 13px;
  color: #666;
  font-style: italic;
}

/* Shared games list */
.sessionList {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 20px;
}
.sessionRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px;
  background: #f8f9fa;
  border-radius: 10px;
  border: 1px solid #eee;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;
}
.sessionRow:hover {
  background: #eef2f7;
  border-color: #3f72af;
}
.sessionInfo {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.sessionGameId {
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 16px;
  color: #112d4e;
}
.sessionMeta {
  font-size: 14px;
  color: #666;
}
.sessionShareMetaRow {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.sessionMetaDivider {
  font-size: 14px;
  color: #8a8a8a;
}
.noGames {
  color: #666;
  margin-top: 20px;
  font-size: 15px;
}

/* Teachers list */
.teacherList {
  display: flex;
  flex-direction: column;
  gap: 0;
}
.teacherItem {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 0;
  border-bottom: 1px solid #eee;
}
.teacherItem:last-of-type {
  border-bottom: none;
}
.teacherInfo {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.teacherName {
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 16px;
  color: #112d4e;
}
.teacherEmail {
  font-size: 14px;
  color: #666;
}
.teacherRole {
  flex-shrink: 0;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 20px;
  background: #e8e8e8;
  color: #555;
}
.teacherRoleMain {
  background: #3f72af;
  color: #fff;
}
.inviteBtnWrap {
  display: flex;
  justify-content: center;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #eee;
}
.inviteMessage {
  margin-top: 12px;
  color: #2d6a4f;
  font-size: 14px;
  text-align: center;
}
/* Invite modal – general info */
.inviteGeneralInfo {
  margin-bottom: 20px;
}
.inviteInfoText {
  font-size: 15px;
  color: #333;
  line-height: 1.5;
  margin-bottom: 16px;
}
.inviteInfoRow {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}
.inviteInfoLabel {
  font-weight: 600;
  font-size: 14px;
  color: #333;
}
.inviteInfoValue {
  font-size: 15px;
  color: #112d4e;
  font-family: monospace;
}
.loading {
  padding: 20px;
  text-align: center;
  color: #666;
}
.modalOverlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  overflow-y: auto;
}
.modal {
  background: #fff;
  border-radius: 12px;
  padding: 28px 32px;
  width: 100%;
  max-width: 440px;
  max-height: calc(100vh - 40px);
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  border: 1px solid #e8e8e8;
  margin: auto 0;
}
.shareModal {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  overflow: hidden;
  min-height: 0;
}
.shareModalBody {
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;
  padding-bottom: 28px;
  margin-right: -4px;
}
/* Filter popup: wider so content doesn't wrap (must come after .modal) */
.filterModal {
  max-width: 820px;
  width: 95%;
}
.modalTitle {
  font-family: "Poppins", sans-serif;
  font-size: 22px;
  font-weight: 600;
  color: #112d4e;
  margin-bottom: 24px;
  text-align: center;
}
.formGroup {
  margin-bottom: 20px;
}
.formLabel {
  display: block;
  margin-bottom: 8px;
  font-family: "Poppins", sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #333;
}
.formInput {
  display: block;
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 15px;
  font-family: "Poppins", sans-serif;
  box-sizing: border-box;
  background: #fff;
}
.formInput:focus {
  outline: none;
  border-color: #3f72af;
  box-shadow: 0 0 0 2px rgba(63, 114, 175, 0.2);
}
.formInput::placeholder {
  color: #999;
}
.formGroup textarea.formInput {
  resize: vertical;
  min-height: 72px;
}
.errorText {
  color: #c00;
  font-size: 14px;
  margin-bottom: 12px;
}
.deleteConfirmText {
  font-size: 15px;
  color: #333;
  margin-bottom: 8px;
}
.deleteGroupNameRef {
  font-weight: 600;
  color: #112d4e;
  margin-bottom: 16px;
  font-size: 15px;
}
.emailChipContainer {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  min-height: 44px;
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 8px;
  background: #fff;
  cursor: text;
}
.emailChipContainer:focus-within {
  border-color: #3f72af;
  box-shadow: 0 0 0 2px rgba(63, 114, 175, 0.2);
}
.emailChip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: #e8eef5;
  border: 1px solid #3f72af;
  border-radius: 6px;
  font-size: 14px;
  color: #112d4e;
}
.chipRemove {
  padding: 0;
  margin: 0;
  margin-left: 2px;
  background: none;
  border: none;
  font-size: 18px;
  line-height: 1;
  color: #666;
  cursor: pointer;
}
.chipRemove:hover {
  color: #c00;
}
.emailChipInput {
  flex: 1;
  min-width: 180px;
  padding: 6px 0;
  border: none;
  outline: none;
  font-size: 15px;
  font-family: "Poppins", sans-serif;
}
.emailChipInput::placeholder {
  color: #999;
}
.modalActions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 8px;
}
.modalActionsSpread {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0;
  padding-top: 16px;
  padding-bottom: 4px;
  border-top: 1px solid #e8e8e8;
  background: #fff;
  flex-shrink: 0;
}
.modalEmpty {
  color: #666;
  font-size: 14px;
  margin: 12px 0;
}
.gamePickList {
  max-height: 200px;
  overflow-y: auto;
}
.shareModalGamePickList {
  max-height: none;
  overflow: visible;
}
.gamePickItem {
  padding: 12px;
  border: 1px solid #eee;
  border-radius: 8px;
  margin-bottom: 8px;
  cursor: pointer;
}
.gamePickRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.gamePickItem:hover {
  background: #f0f0f0;
}
.previewBtn {
  flex-shrink: 0;
  padding: 6px 12px;
  border: 1px solid #d0d7e2;
  border-radius: 8px;
  background: #fff;
  color: #112d4e;
  font-family: "Poppins", sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.previewBtn:hover {
  border-color: #3f72af;
  color: #3f72af;
}
.gamePickItemDisabled {
  opacity: 0.65;
  pointer-events: none;
}
.gamePickItemSelected {
  border-color: #3f72af;
  background: #e8eef5;
}
.shareModeTabs {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}
.shareModeTab {
  padding: 8px 16px;
  border: 1px solid #ccc;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  font-size: 14px;
}
.shareModeTabActive {
  border-color: #3f72af;
  background: #e8eef5;
  color: #112d4e;
}
.modalHint {
  font-size: 14px;
  color: #666;
  margin-bottom: 12px;
}
.personalShareLabel {
  display: block;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 6px;
  color: #333;
}
.personalShareInput {
  display: block;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 15px;
  margin-bottom: 12px;
  box-sizing: border-box;
}
.personalShareError {
  color: #c00;
  font-size: 14px;
  margin: 8px 0;
}
.inlineShareActions {
  display: flex;
  justify-content: flex-end;
  margin-top: 18px;
  padding-top: 8px;
}
</style>
